import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { BLOG_LENGTHS, type BlogArticleLength } from '@jeraisonne/db';
import { useGenerateBlogArticle } from '@/lib/admin/use-blog';
import { PIPELINE_MODELS } from '@/lib/admin/pipeline-models';

const inputCls =
  'border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2 w-full';
const labelCls = 'font-display text-ink-700 text-[12px] font-semibold';
const areaCls =
  'border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 w-full resize-y rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2';

export function TutorBlogNewPage() {
  const navigate = useNavigate();
  const generate = useGenerateBlogArticle();

  const [subject, setSubject] = useState('');
  const [sources, setSources] = useState('');
  const [references, setReferences] = useState('');
  const [tone, setTone] = useState('');
  const [audience, setAudience] = useState('');
  const [length, setLength] = useState<BlogArticleLength>('normal');
  const [model, setModel] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!subject.trim()) return;
    const res = await generate.mutateAsync({
      subject: subject.trim(),
      sources: sources.trim() || undefined,
      references: references.trim() || undefined,
      options:
        tone.trim() || audience.trim()
          ? { tone: tone.trim() || undefined, audience: audience.trim() || undefined }
          : undefined,
      length,
      model: model || undefined,
    });
    navigate({ to: '/tutor/blog/$articleId', params: { articleId: res.article_id } });
  }

  return (
    <div className="mx-auto max-w-xl p-4 sm:p-6">
      <Link
        to="/tutor/blog"
        className="text-ink-500 hover:text-ink-900 mb-5 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Blog réflexif
      </Link>

      <h1 className="font-display text-ink-900 mb-1 text-[22px] font-extrabold">Nouvel article</h1>
      <p className="text-ink-500 mb-6 font-sans text-[13px]">
        L'IA construit un parcours de réflexion (5 blocs + 4 sondages, penseurs, ressources, réseaux
        sociaux, SEO). La génération prend ~30 s.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>Sujet / question centrale</label>
          <textarea
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            rows={2}
            placeholder="ex. L'IA rend-elle les élèves plus intelligents ou plus dépendants ?"
            className={areaCls}
            required
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>
            Sources{' '}
            <span className="text-ink-400 font-normal">(optionnel — texte / URLs collés)</span>
          </label>
          <textarea
            value={sources}
            onChange={(e) => setSources(e.target.value)}
            rows={4}
            placeholder="Colle ici des extraits d'articles, études, rapports, URLs… L'IA les croisera et reformulera (jamais de copie)."
            className={areaCls}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>
            Penseurs / références suggérés{' '}
            <span className="text-ink-400 font-normal">(optionnel)</span>
          </label>
          <input
            type="text"
            value={references}
            onChange={(e) => setReferences(e.target.value)}
            placeholder="ex. Dehaene, Kahneman, Montessori"
            className={inputCls}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <label className={labelCls}>
              Ton <span className="text-ink-400 font-normal">(optionnel)</span>
            </label>
            <input
              type="text"
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              placeholder="éditorial, accessible…"
              className={inputCls}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className={labelCls}>
              Lectorat <span className="text-ink-400 font-normal">(optionnel)</span>
            </label>
            <input
              type="text"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              placeholder="parents, lycéens, profs…"
              className={inputCls}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>
            Longueur de l'article{' '}
            <span className="text-ink-400 font-normal">(structure inchangée)</span>
          </label>
          <select
            value={length}
            onChange={(e) => setLength(e.target.value as BlogArticleLength)}
            className="border-ink-200 text-ink-900 focus:ring-primary-400 w-full rounded-xl border bg-white px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
          >
            {BLOG_LENGTHS.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>
            Modèle IA <span className="text-ink-400 font-normal">(défaut : Opus)</span>
          </label>
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="border-ink-200 text-ink-900 focus:ring-primary-400 w-full rounded-xl border bg-white px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
          >
            <option value="">Par défaut (Opus — qualité éditoriale)</option>
            {Object.entries(PIPELINE_MODELS).map(([id, m]) => (
              <option key={id} value={id}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-3 pt-2">
          <Link to="/tutor/blog" className="flex-1">
            <Button type="button" variant="outline" className="w-full">
              Annuler
            </Button>
          </Link>
          <Button
            type="submit"
            className="flex flex-1 items-center justify-center gap-1.5"
            disabled={generate.isPending || !subject.trim()}
          >
            <Sparkles className="h-4 w-4" />
            {generate.isPending ? 'Génération…' : 'Générer'}
          </Button>
        </div>

        {generate.isError && (
          <p className="text-accent-600 font-sans text-[13px]">{generate.error?.message}</p>
        )}
      </form>
    </div>
  );
}
